import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { API_BASE_URL } from '../../config';

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const NewsroomPlanning = () => {
  const navigate = useNavigate();
  const [taxonomy, setTaxonomy] = useState({ practices: [] });
  const [calendars, setCalendars] = useState([]);
  const [focuses, setFocuses] = useState([]);
  const [cases, setCases] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState('');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [calendarForm, setCalendarForm] = useState({
    name: '', practice_id: '', period_start: '', period_end: '',
    weekly_publication_target: 30
  });
  const [focusForm, setFocusForm] = useState({
    primary_vertical_id: '', week_start: '', title: '', editorial_question: '',
    target_articles: 3, search_queries: '', rss_feeds: ''
  });

  const selectedCalendar = calendars.find((item) => item.id === selectedCalendarId);
  const selectedPractice = useMemo(
    () => taxonomy.practices?.find((item) => item.id === selectedCalendar?.practice_id),
    [taxonomy, selectedCalendar]
  );

  const load = async () => {
    try {
      const [taxonomyResponse, calendarResponse, focusResponse, caseResponse, jobResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/market-intelligence/taxonomy`),
        axios.get(`${API_BASE_URL}/api/admin/editorial/calendars`, authConfig()),
        axios.get(`${API_BASE_URL}/api/admin/editorial/focuses`, authConfig()),
        axios.get(`${API_BASE_URL}/api/admin/newsroom/cases`, authConfig()),
        axios.get(`${API_BASE_URL}/api/admin/newsroom/jobs`, authConfig())
      ]);
      setTaxonomy(taxonomyResponse.data.data || { practices: [] });
      setCalendars(calendarResponse.data.data || []);
      setFocuses(focusResponse.data.data || []);
      setCases(caseResponse.data.data || []);
      setJobs(jobResponse.data.data || []);
      if (!selectedCalendarId && calendarResponse.data.data?.length) {
        setSelectedCalendarId(calendarResponse.data.data[0].id);
      }
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  useEffect(() => {
    load();
    const interval = window.setInterval(load, 5000);
    return () => window.clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runAction = async (key, action, successMessage) => {
    setBusy(key);
    setMessage('');
    setError('');
    try {
      await action();
      setMessage(successMessage);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const createCalendar = (event) => {
    event.preventDefault();
    runAction('calendar-create', () => axios.post(
      `${API_BASE_URL}/api/admin/editorial/calendars`, calendarForm, authConfig()
    ), 'Editorial calendar created. Approve it before approving weekly focuses.');
  };

  const createFocus = (event) => {
    event.preventDefault();
    if (!selectedCalendar) return;
    const payload = {
      ...focusForm,
      calendar_id: selectedCalendar.id,
      practice_id: selectedCalendar.practice_id,
      search_queries: focusForm.search_queries.split('\n').map((v) => v.trim()).filter(Boolean),
      rss_feeds: focusForm.rss_feeds.split('\n').map((v) => v.trim()).filter(Boolean)
    };
    runAction('focus-create', () => axios.post(
      `${API_BASE_URL}/api/admin/editorial/focuses`, payload, authConfig()
    ), 'Weekly focus created.');
  };

  const focusCases = (focusId) => cases.filter((item) => item.focus_id === focusId);
  const latestJob = (caseId) => jobs.find((item) => item.case_id === caseId);
  const visibleFocuses = focuses.filter((item) => item.calendar_id === selectedCalendarId);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-orange-600">ALICE Editorial Office</p>
            <h1 className="text-3xl font-bold text-slate-950">Newsroom planning</h1>
            <p className="mt-1 text-slate-600">Set the agenda, approve weekly focuses and commission agent research.</p>
          </div>
          <button onClick={() => navigate('/admin/market-intelligence')} className="rounded-lg border border-slate-300 bg-white px-4 py-2 font-semibold text-slate-700">
            Human review queue
          </button>
        </div>

        {message && <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-800">{message}</div>}
        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">{error}</div>}

        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={createCalendar} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900">Create editorial calendar</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input className="rounded-lg border p-2" placeholder="Calendar name" value={calendarForm.name} onChange={(e) => setCalendarForm({ ...calendarForm, name: e.target.value })} />
              <select required className="rounded-lg border p-2" value={calendarForm.practice_id} onChange={(e) => setCalendarForm({ ...calendarForm, practice_id: e.target.value })}>
                <option value="">Select practice</option>
                {taxonomy.practices?.map((practice) => <option key={practice.id} value={practice.id}>{practice.name}</option>)}
              </select>
              <label className="text-sm text-slate-600">Period start<input required type="date" className="mt-1 w-full rounded-lg border p-2" value={calendarForm.period_start} onChange={(e) => setCalendarForm({ ...calendarForm, period_start: e.target.value })} /></label>
              <label className="text-sm text-slate-600">Period end<input required type="date" className="mt-1 w-full rounded-lg border p-2" value={calendarForm.period_end} onChange={(e) => setCalendarForm({ ...calendarForm, period_end: e.target.value })} /></label>
              <label className="text-sm text-slate-600">Weekly publication target<input required min="1" type="number" className="mt-1 w-full rounded-lg border p-2" value={calendarForm.weekly_publication_target} onChange={(e) => setCalendarForm({ ...calendarForm, weekly_publication_target: Number(e.target.value) })} /></label>
            </div>
            <button disabled={busy === 'calendar-create'} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 font-semibold text-white disabled:opacity-50">Create calendar</button>
          </form>

          <form onSubmit={createFocus} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900">Create weekly focus</h2>
            <select required className="mt-4 w-full rounded-lg border p-2" value={selectedCalendarId} onChange={(e) => setSelectedCalendarId(e.target.value)}>
              <option value="">Select calendar</option>
              {calendars.map((calendar) => <option key={calendar.id} value={calendar.id}>{calendar.name || calendar.period_start} — {calendar.status}</option>)}
            </select>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <select required className="rounded-lg border p-2" value={focusForm.primary_vertical_id} onChange={(e) => setFocusForm({ ...focusForm, primary_vertical_id: e.target.value })}>
                <option value="">Select vertical</option>
                {selectedPractice?.verticals?.map((vertical) => <option key={vertical.id} value={vertical.id}>{vertical.name}</option>)}
              </select>
              <input required type="date" className="rounded-lg border p-2" value={focusForm.week_start} onChange={(e) => setFocusForm({ ...focusForm, week_start: e.target.value })} />
              <input required className="rounded-lg border p-2 sm:col-span-2" placeholder="Focus: AI and Commodity Trading Transformation" value={focusForm.title} onChange={(e) => setFocusForm({ ...focusForm, title: e.target.value })} />
              <textarea required className="rounded-lg border p-2 sm:col-span-2" placeholder="Editorial question" value={focusForm.editorial_question} onChange={(e) => setFocusForm({ ...focusForm, editorial_question: e.target.value })} />
              <input min="1" type="number" className="rounded-lg border p-2" value={focusForm.target_articles} onChange={(e) => setFocusForm({ ...focusForm, target_articles: Number(e.target.value) })} />
              <textarea className="rounded-lg border p-2" placeholder="Search queries, one per line" value={focusForm.search_queries} onChange={(e) => setFocusForm({ ...focusForm, search_queries: e.target.value })} />
              <textarea className="rounded-lg border p-2 sm:col-span-2" placeholder="RSS feed URLs, one per line" value={focusForm.rss_feeds} onChange={(e) => setFocusForm({ ...focusForm, rss_feeds: e.target.value })} />
            </div>
            <button disabled={!selectedCalendar || busy === 'focus-create'} className="mt-4 rounded-lg bg-orange-600 px-4 py-2 font-semibold text-white disabled:opacity-50">Create weekly focus</button>
          </form>
        </div>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><h2 className="text-xl font-bold text-slate-900">Calendar and production pipeline</h2><p className="text-sm text-slate-600">Human approval controls the calendar, topic selection and publication.</p></div>
            {selectedCalendar && selectedCalendar.status !== 'approved' && <button onClick={() => runAction(`approve-calendar-${selectedCalendar.id}`, () => axios.post(`${API_BASE_URL}/api/admin/editorial/calendars/${selectedCalendar.id}/approve`, {}, authConfig()), 'Calendar approved.')} className="rounded-lg bg-emerald-700 px-4 py-2 font-semibold text-white">Approve calendar</button>}
          </div>

          <div className="mt-5 space-y-4">
            {visibleFocuses.length === 0 && <p className="rounded-lg bg-slate-50 p-5 text-slate-500">No weekly focuses in this calendar yet.</p>}
            {visibleFocuses.map((focus) => (
              <div key={focus.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex flex-wrap justify-between gap-3">
                  <div><p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Week of {focus.week_start} · {focus.vertical_name}</p><h3 className="text-lg font-bold text-slate-900">{focus.title}</h3><p className="text-sm text-slate-600">{focus.editorial_question}</p></div>
                  <div className="flex items-start gap-2">
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold">Target {focus.target_articles}</span>
                    {focus.status !== 'approved' ? <button onClick={() => runAction(`approve-focus-${focus.id}`, () => axios.post(`${API_BASE_URL}/api/admin/editorial/focuses/${focus.id}/approve`, {}, authConfig()), 'Weekly focus approved.')} className="rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white">Approve focus</button> : <button onClick={() => runAction(`discover-${focus.id}`, () => axios.post(`${API_BASE_URL}/api/admin/editorial/focuses/${focus.id}/start-discovery`, {}, authConfig()), 'Discovery queued. You can leave this page while the newsroom worker runs.')} className="rounded-lg bg-orange-600 px-3 py-1.5 text-sm font-semibold text-white">Queue discovery</button>}
                  </div>
                </div>

                {focusCases(focus.id).map((newsroomCase) => (
                  <div key={newsroomCase.id} className="mt-4 rounded-lg bg-slate-50 p-4">
                    <div className="flex justify-between"><span className="font-semibold text-slate-800">Case {newsroomCase.id.slice(0, 8)}</span><span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">{newsroomCase.state.replaceAll('_', ' ')}</span></div>
                    <CaseActions newsroomCase={newsroomCase} job={latestJob(newsroomCase.id)} runAction={runAction} busy={busy} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

const CaseActions = ({ newsroomCase, job, runAction }) => {
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (newsroomCase.state === 'awaiting_topic_selection') {
      axios.get(`${API_BASE_URL}/api/admin/newsroom/cases/${newsroomCase.id}`, authConfig())
        .then((response) => setDetails(response.data.data))
        .catch(() => setDetails(null));
    }
  }, [newsroomCase.id, newsroomCase.state]);

  if (job && ['queued', 'retrying', 'running'].includes(job.status)) {
    return <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900"><div className="flex items-center justify-between gap-3"><span className="font-semibold">{job.progress?.message || 'Newsroom worker processing'}</span><span className="rounded-full bg-white px-2 py-1 text-xs uppercase">{job.status}</span></div>{job.status === 'retrying' && job.last_error && <p className="mt-2 text-xs text-red-700">{job.last_error}</p>}</div>;
  }

  if (job?.status === 'failed') {
    return <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"><p className="font-semibold">Background job failed</p><p className="mt-1">{job.last_error}</p></div>;
  }

  if (newsroomCase.state === 'topic_selected') {
    return <p className="mt-3 text-sm text-slate-600">Production is queued automatically after topic selection.</p>;
  }

  if (newsroomCase.state !== 'awaiting_topic_selection') return null;
  return <div className="mt-3 space-y-2">{details?.opportunities?.map((item) => <div key={item.id} className="rounded-lg border bg-white p-3"><div className="flex flex-wrap items-start justify-between gap-2"><div className="max-w-3xl"><p className="font-semibold text-slate-900">#{item.rank} {item.headline}</p><p className="text-sm text-slate-600">{item.angle}</p><p className="mt-1 text-xs text-slate-500">Confidence {item.confidence_score}% · {item.source_count} source(s)</p></div><button onClick={() => runAction(`select-${item.id}`, () => axios.post(`${API_BASE_URL}/api/admin/newsroom/opportunities/${item.id}/select`, {}, authConfig()), 'Story selected. Production has been queued automatically.')} className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white">Select and produce</button></div></div>)}</div>;
};

export default NewsroomPlanning;
